/**
 * Flow Utility Functions
 * Extracted from Flow.vue for reuse across Flow Editor, Flow Compare, etc.
 */

/**
 * Generate a GUID/UUID v4 string
 * @returns {string} A new GUID
 */
export function generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Default schema template for Advanced Router nodes
 * @returns {Object}
 */
export function getDefaultAdvancedRouterSchemaTemplate() {
    return {
        type: 'OBJECT',
        properties: {
            intents: {
                type: 'ARRAY',
                items: {
                    type: 'OBJECT',
                    properties: {
                        capability_intent: { type: 'STRING' },
                        confidence: { type: 'NUMBER' },
                        intents: {
                            type: 'ARRAY',
                            items: {
                                type: 'OBJECT',
                                properties: {
                                    capability_intent: { type: 'STRING' },
                                    confidence: { type: 'NUMBER' },
                                    intents: {
                                        type: 'ARRAY',
                                        items: {}
                                    }
                                },
                                required: ['capability_intent', 'confidence', 'intents']
                            }
                        }
                    },
                    required: ['capability_intent', 'confidence', 'intents']
                }
            }
        },
        required: ['intents']
    };
}

/**
 * Expand node data from compact DB format to full Vue component format.
 * Restores defaults, converts negative memoryDuration to positive, etc.
 * 
 * @param {string} nodeType - The node type (e.g., 'model', 'intent', etc.)
 * @param {Object} data - Compact node data from server
 * @returns {Object} Expanded node data with all defaults
 */
export function expandNodeData(nodeType, data) {
    const expanded = { ...data };

    switch (nodeType) {
        case 'messageInput':
            expanded.selectedModel = Array.isArray(data.modelDisplayId)
                ? data.modelDisplayId
                : (data.modelDisplayId ? [data.modelDisplayId] : []);
            break;
        case 'model':
            expanded.selectedModel = data.modelDisplayId;
            expanded.provider = data.provider || null;
            expanded.model = data.model || null;
            expanded.apiVersion = data.apiVersion || null;
            expanded.systemPrompt = data.systemPrompt || '';
            expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
            expanded.temperature = data.temperature ?? null;
            expanded.maxTokens = data.maxTokens ?? null;
            expanded.topP = data.topP ?? null;
            expanded.frequencyPenalty = data.frequencyPenalty ?? null;
            expanded.presencePenalty = data.presencePenalty ?? null;
            expanded.stopSequence = data.stopSequence || null;
            expanded.gender = data.gender || 'male';
            expanded.typingSpeed = data.typingSpeed ?? null;
            expanded.maxInputLength = data.maxInputLength ?? 200000;
            expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
            expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
            expanded.bypassOnError = data.bypassOnError !== undefined ? data.bypassOnError : false;
            expanded.enableTime = data.enableTime ?? false;
            expanded.timeZone = data.timeZone || null;
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            expanded.memoryTurn = data.memoryTurn ?? null;
            expanded.memoryDuration = data.memoryDuration < 0
                ? Math.abs(data.memoryDuration)
                : data.memoryDuration;
            expanded.shortTermMemory = data.shortTermMemory ?? null;
            expanded.longTermMemory = data.longTermMemory !== undefined ? data.longTermMemory : false;
            expanded.isChainMassage = data.isChainMassage !== undefined ? data.isChainMassage : false;
            expanded.lenChainMassage = data.lenChainMassage ?? null;
            expanded.timeSecondChainMassage = data.timeSecondChainMassage ?? null;
            expanded.loadingBar = data.loadingBar !== undefined ? data.loadingBar : false;
            expanded.loadingBarDuration = data.loadingBarDuration ?? 0;
            expanded.showLineName = data.showLineName !== undefined ? data.showLineName : false;
            expanded.monitorLineUserId = data.monitorLineUserId || null;
            expanded.monitorSocialID = data.monitorSocialID || null;
            expanded.forbiddenWords = data.forbiddenWords || [''];
            break;
        case 'taxPlanningV2':
            // Node label
            expanded.taxPlanningV2Name = data.taxPlanningV2Name || '';
            // LLM configuration (same shared keys as the AI Model node). The V2 node no longer
            // READS these at runtime — the LLM config comes from the resolved settings version —
            // but they are shared keys used by every LLM-ish node type, so they are kept here so
            // legacy node data round-trips untouched instead of being wiped on the next save.
            expanded.selectedModel = data.modelDisplayId;
            expanded.provider = data.provider || null;
            expanded.model = data.model || null;
            expanded.apiVersion = data.apiVersion || null;
            expanded.systemPrompt = data.systemPrompt || '';
            expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
            expanded.temperature = data.temperature ?? null;
            expanded.maxTokens = data.maxTokens ?? null;
            expanded.topP = data.topP ?? null;
            expanded.frequencyPenalty = data.frequencyPenalty ?? null;
            expanded.presencePenalty = data.presencePenalty ?? null;
            expanded.stopSequence = data.stopSequence || null;
            expanded.gender = data.gender || 'male';
            expanded.memoryTurn = data.memoryTurn ?? null;
            expanded.memoryDuration = data.memoryDuration < 0
                ? Math.abs(data.memoryDuration)
                : data.memoryDuration;
            expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
            expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
            expanded.bypassOnError = data.bypassOnError !== undefined ? data.bypassOnError : false;
            expanded.enableTime = data.enableTime ?? false;
            expanded.timeZone = data.timeZone || null;
            // Tax-specific configuration (V2) now lives in `tax.TaxPlanningSettingsV2` as a
            // versioned settings row — the node only pins WHICH version it wants.
            // `null` = use the version that is currently active. This key MUST stay listed
            // here (and in Flow.vue's extractNodeData whitelist) or the pinned version is
            // silently dropped on save/load.
            expanded.taxPlanningV2SettingsVersionId = data.taxPlanningV2SettingsVersionId ?? null;
            break;
        case 'intentAI':
            expanded.intentaiProvider = data.intentaiProvider || data.provider || null;
            expanded.intentaiModel = Array.isArray(data.intentaiModel)
                ? (data.intentaiModel.length > 0 ? data.intentaiModel[0] : null)
                : (data.intentaiModel || data.model || null);
            expanded.intentAiModelApiVersion = data.intentAiModelApiVersion || null;
            expanded.intentaiSystemPrompt = data.intentaiSystemPrompt || data.intentSystemPrompt || '';
            expanded.intentaiExamples = data.intentaiExamples || data.intentExamples || '';
            expanded.intentaiConfidence = data.intentaiConfidence ?? data.intentConfidence ?? 75;
            expanded.intentaiFallbackIntent = data.intentaiFallbackIntent || data.fallbackIntent || 'unknown';
            expanded.intentaiTemperature = data.intentaiTemperature ?? data.temperature ?? null;
            expanded.intentaiMaxTokens = data.intentaiMaxTokens ?? data.maxTokens ?? null;
            expanded.intentaiTopP = data.intentaiTopP ?? data.topP ?? null;
            expanded.intentaiFrequencyPenalty = data.intentaiFrequencyPenalty ?? data.frequencyPenalty ?? null;
            expanded.intentaiPresencePenalty = data.intentaiPresencePenalty ?? data.presencePenalty ?? null;
            expanded.intentaiStopSequence = data.intentaiStopSequence || data.stopSequence || null;
            expanded.intentaiGender = data.intentaiGender || data.gender || 'male';
            expanded.intentaiTypingSpeed = data.intentaiTypingSpeed ?? data.typingSpeed ?? null;
            expanded.intentaiMaxInputLength = data.intentaiMaxInputLength ?? data.maxInputLength ?? 200000;
            expanded.intentaiStreamResponse = data.intentaiStreamResponse !== undefined ? data.intentaiStreamResponse : (data.streamResponse !== undefined ? data.streamResponse : false);
            expanded.intentaiJsonResponse = data.intentaiJsonResponse !== undefined ? data.intentaiJsonResponse : (data.jsonResponse !== undefined ? data.jsonResponse : false);
            expanded.intentaiEnableTime = data.intentaiEnableTime !== undefined ? data.intentaiEnableTime : (data.enableTime ?? false);
            expanded.intentaiTimeZone = data.intentaiTimeZone || data.timeZone || null;
            expanded.intentaiEnableTransactionId = data.intentaiEnableTransactionId !== undefined ? data.intentaiEnableTransactionId : (data.enableTransactionId ?? false);
            expanded.intentaiMemoryTurn = data.intentaiMemoryTurn ?? data.memoryTurn ?? null;
            expanded.intentaiMemoryDuration = data.intentaiMemoryDuration < 0
                ? Math.abs(data.intentaiMemoryDuration)
                : (data.memoryDuration < 0 ? Math.abs(data.memoryDuration) : data.intentaiMemoryDuration ?? data.memoryDuration);
            expanded.intentaiShortTermMemory = data.intentaiShortTermMemory ?? data.shortTermMemory ?? null;
            expanded.intentaiLongTermMemory = data.intentaiLongTermMemory !== undefined ? data.intentaiLongTermMemory : (data.longTermMemory !== undefined ? data.longTermMemory : false);
            expanded.intentaiIsChainMassage = data.intentaiIsChainMassage !== undefined ? data.intentaiIsChainMassage : (data.isChainMassage !== undefined ? data.isChainMassage : false);
            expanded.intentaiLenChainMassage = data.intentaiLenChainMassage || data.lenChainMassage || null;
            expanded.intentaiTimeSecondChainMassage = data.intentaiTimeSecondChainMassage || data.timeSecondChainMassage || null;
            expanded.intentaiShowLineName = data.intentaiShowLineName !== undefined ? data.intentaiShowLineName : (data.showLineName !== undefined ? data.showLineName : false);
            expanded.intentaiLoadingBar = data.intentaiLoadingBar !== undefined ? data.intentaiLoadingBar : (data.loadingBar !== undefined ? data.loadingBar : false);
            expanded.intentaiLoadingBarDuration = data.intentaiLoadingBarDuration || data.loadingBarDuration || 0;
            expanded.intentaiMonitorLineUserId = data.intentaiMonitorLineUserId || data.monitorLineUserId || null;
            expanded.intentaiMonitorSocialID = data.intentaiMonitorSocialID || data.monitorSocialID || null;
            expanded.intentaiForbiddenWords = data.intentaiForbiddenWords || data.forbiddenWords || [''];
            break;
        case 'intent':
            expanded.intents = data.intents || [];
            expanded.actions = data.actions || [];
            expanded.outputs = data.outputs || [];
            break;
        case 'smartIntent':
            expanded.smartIntentGroupName = data.smartIntentGroupName || '';
            expanded.smartIntents = data.smartIntents || [];
            if (expanded.smartIntents && expanded.smartIntents.length > 0) {
                expanded.smartIntents.forEach(smartIntent => {
                    if (smartIntent.mainAction) {
                        if (smartIntent.mainAction.apiVersion === undefined) {
                            smartIntent.mainAction.apiVersion = null;
                        }
                        if (smartIntent.mainAction.forbiddenWords && Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                            const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                            if (lastItem !== '') {
                                smartIntent.mainAction.forbiddenWords.push('');
                            }
                        } else {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    }
                });
            }
            expanded.smartIntentFallback = data.smartIntentFallback || {
                useAI: false, provider: null, model: null, apiVersion: null,
                gender: 'male', typingSpeed: null, maxInputLength: 200000,
                streamResponse: false, jsonResponse: false, enableTime: null,
                timeZone: null, enableTransactionId: null, temperature: null,
                topP: null, frequencyPenalty: null, presencePenalty: null,
                maxTokens: null, stopSequence: null, memoryTurn: null,
                memoryDuration: null, shortTermMemory: null, longTermMemory: false,
                isChainMassage: false, lenChainMassage: null,
                timeSecondChainMassage: null, forbiddenWords: ['']
            };
            if (expanded.smartIntentFallback.forbiddenWords && Array.isArray(expanded.smartIntentFallback.forbiddenWords)) {
                const lastItem = expanded.smartIntentFallback.forbiddenWords[expanded.smartIntentFallback.forbiddenWords.length - 1];
                if (lastItem !== '') {
                    expanded.smartIntentFallback.forbiddenWords.push('');
                }
            } else {
                expanded.smartIntentFallback.forbiddenWords = [''];
            }
            break;
        case 'contactAgent':
        case 'contactAgentV2': // V2 reads/writes the same data keys as V1 — only its handles differ
            expanded.departmentName = data.departmentName || '';
            expanded.contactAgentMessage = data.contactAgentMessage || '';
            expanded.contactAgentMessageChinese = data.contactAgentMessageChinese || '';
            expanded.contactAgentMessageEnglish = data.contactAgentMessageEnglish || '';
            expanded.handoffMessage = data.handoffMessage || '';
            expanded.botDisableTime = data.botDisableTime || null;
            expanded.disableBot = data.disableBot !== undefined ? data.disableBot : true;
            break;
        case 'apiTrigger':
            expanded.apiUrl = data.apiUrl || '';
            expanded.httpMethod = data.httpMethod || 'GET';
            expanded.headers = data.headers || '';
            expanded.textResponse = data.textResponse || '';
            expanded.jsonValidate = data.jsonValidate || '{\n   "Name":"Value"\n}';
            expanded.modelJsonValidator = data.modelJsonValidator || null;
            expanded.listHeadersAPITrigger = data.listHeadersAPITrigger || [{ key: '', value: '' }];
            expanded.listValidation = data.listValidation || [{ key: '', typeValidation: '', numberOfCharacter: '', regExp: '', promptMessage: '' }];
            break;
        case 'products':
            expanded.productSourceType = data.productSourceType || 'product';
            expanded.productGroupCollectionId = data.productGroupCollectionId || null;
            expanded.productGroupCollectionName = data.productGroupCollectionName || '';
            expanded.productGroupList = data.productGroupList || [];
            expanded.productTopNChunks = data.productTopNChunks || 5;
            expanded.productCosineSimilarity = data.productCosineSimilarity || 0.7;
            break;
        case 'documents':
            expanded.document = data.document || [];
            expanded.textReplacement = data.textReplacement || [];
            expanded.topNChunks = data.topNChunks || 5;
            expanded.cosineSimilarity = data.cosineSimilarity || 0.7;
            expanded.selectedDocumentData = data.selectedDocumentData || [];
            expanded.enableSpecificDataSelection = data.enableSpecificDataSelection || false;
            expanded.documentVectorName = '';
            expanded.communityId = data.communityId || null;
            expanded.communityTopGroups = data.communityTopGroups || 3;
            expanded.communityTopChunks = data.communityTopChunks || 5;
            expanded.communitySearchMethod = data.communitySearchMethod || 'topn';
            expanded.communityMinCosineScore = data.communityMinCosineScore || 0.7;
            expanded.metaCommunityId = data.metaCommunityId || null;
            expanded.metaCommunityTopGroups = data.metaCommunityTopGroups || 3;
            expanded.metaCommunityTopChunks = data.metaCommunityTopChunks || 5;
            expanded.metaCommunitySearchMethod = data.metaCommunitySearchMethod || 'topn';
            expanded.metaCommunityMinCosineScore = data.metaCommunityMinCosineScore || 0.7;
            expanded.filterTopic = data.filterTopic || null;
            expanded.filterSubTopic = data.filterSubTopic || null;
            expanded.filterMode = data.filterMode || 'include';
            break;
        case 'apiknowledge':
            expanded.knowledgeApiUrl = data.knowledgeApiUrl || '';
            expanded.knowledgeHttpMethod = data.knowledgeHttpMethod || 'GET';
            expanded.knowledgeRequestBody = data.knowledgeRequestBody || '';
            expanded.responseDataPath = data.responseDataPath || 'data';
            expanded.useLHBService = data.useLHBService || false;
            expanded.listHeaders = data.listHeaders || [];
            expanded.description = data.description || '';
            break;
        case 'email':
            expanded.emailProvider = data.emailProvider || 'mailgun';
            expanded.toEmail = data.toEmail || '';
            expanded.ccEmails = data.ccEmails || [];
            expanded.bccEmails = data.bccEmails || [];
            expanded.subject = data.subject || '';
            expanded.emailTemplate = data.emailTemplate || 'custom';
            expanded.emailBody = data.emailBody || '';
            expanded.isHtml = data.isHtml !== undefined ? data.isHtml : false;
            break;
        case 'sharepoint':
            expanded.sharepoint = data.sharepoint || {
                siteUrl: '', tenantId: '', clientId: '',
                clientSecret: '', folderPath: '', filename: ''
            };
            break;
        case 'interestOptimizer':
            expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
            expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
            expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            expanded.ioAnswerModel = data.ioAnswerModel || '';
            expanded.ioFallbackPrompt = data.ioFallbackPrompt || '';
            expanded.ioParserModel = data.ioParserModel || '';
            expanded.ioDocuments = data.ioDocuments || [];
            expanded.ioAccountCodes = data.ioAccountCodes || [];
            expanded.ioRequiredFields = data.ioRequiredFields || [];
            break;
        case 'loanInterest':
            expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
            expanded.enableLanguageReplace = data.enableLanguageReplace !== undefined ? data.enableLanguageReplace : false;
            expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            expanded.liAnswerModel = data.liAnswerModel || '';
            expanded.liFallbackPrompt = data.liFallbackPrompt || '';
            expanded.liRequiredFields = data.liRequiredFields || [];
            expanded.liParserModel = data.liParserModel || '';
            expanded.liDocuments = data.liDocuments || [];
            expanded.liInterestRates = data.liInterestRates || [];
            break;
        case 'interestOptimizerV2':
            expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
            expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
            expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            expanded.ioAnswerModel = data.ioAnswerModel || '';
            expanded.ioFallbackPrompt = data.ioFallbackPrompt || '';
            expanded.ioParserModel = data.ioParserModel || '';
            expanded.ioDocuments = data.ioDocuments || [];
            expanded.ioV2MetaDocs = data.ioV2MetaDocs || [];
            expanded.ioRequiredFields = data.ioRequiredFields || [];
            break;
        case 'loanInterestV2':
            expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
            expanded.enableLanguageReplace = data.enableLanguageReplace !== undefined ? data.enableLanguageReplace : false;
            expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            expanded.liAnswerModel = data.liAnswerModel || '';
            expanded.liFallbackPrompt = data.liFallbackPrompt || '';
            expanded.liRequiredFields = data.liRequiredFields || [];
            expanded.liParserModel = data.liParserModel || '';
            expanded.liDocuments = data.liDocuments || [];
            expanded.liV2MetaDocs = data.liV2MetaDocs || [];
            break;
        case 'orchestrator':
            expanded.orchestratorName = data.orchestratorName || '';
            expanded.provider = data.provider || '';
            expanded.model = data.model || '';
            expanded.temperature = data.temperature ?? 0;
            expanded.maxTokens = data.maxTokens ?? null;
            expanded.topP = data.topP ?? 1;
            expanded.streamResponse = data.streamResponse ?? false;
            expanded.jsonResponse = data.jsonResponse ?? false;
            expanded.memoryTurn = data.memoryTurn ?? null;
            expanded.memoryDuration = data.memoryDuration ?? null;
            expanded.shortTermMemory = data.shortTermMemory ?? 200;
            expanded.longTermMemory = data.longTermMemory ?? false;
            expanded.systemPrompt = data.systemPrompt || '';
            expanded.guardrailPrompt = data.guardrailPrompt || '';
            // Selection prompts
            expanded.orchSingleSelectionTemplate = data.orchSingleSelectionTemplate || '';
            expanded.orchPipelineSelectionTemplate = data.orchPipelineSelectionTemplate || '';
            expanded.orchVerifySelectionTemplate = data.orchVerifySelectionTemplate || '';
            expanded.orchAgentListItemTemplate = data.orchAgentListItemTemplate || '';
            expanded.orchAgentNoToolsLabel = data.orchAgentNoToolsLabel || '';
            // Conversation state
            expanded.orchConversationStateTemplate = data.orchConversationStateTemplate || '';
            expanded.orchConversationStateFactTemplate = data.orchConversationStateFactTemplate || '';
            expanded.orchCompanionNote = data.orchCompanionNote || '';
            // Sub-goal hand-off
            expanded.orchSequentialContextTemplate = data.orchSequentialContextTemplate || '';
            expanded.orchCheckerGoalTemplate = data.orchCheckerGoalTemplate || '';
            expanded.orchFallbackSubGoal = data.orchFallbackSubGoal || '';
            // Aggregation
            expanded.orchAggregationTemplate = data.orchAggregationTemplate || '';
            expanded.orchStrategyHintParallel = data.orchStrategyHintParallel || '';
            expanded.orchStrategyHintPipeline = data.orchStrategyHintPipeline || '';
            expanded.orchAggregationFallbackItemTemplate = data.orchAggregationFallbackItemTemplate || '';
            // Guardrail
            expanded.orchGuardrailFormatSuffix = data.orchGuardrailFormatSuffix || '';
            expanded.orchGuardrailUserTemplate = data.orchGuardrailUserTemplate || '';
            // Direct LLM fallback
            expanded.orchDirectLlmPrompt = data.orchDirectLlmPrompt || '';
            // Customer-facing messages
            expanded.orchBlockedMessage = data.orchBlockedMessage || '';
            expanded.orchDirectLlmEmptyMessage = data.orchDirectLlmEmptyMessage || '';
            expanded.orchDirectLlmErrorMessage = data.orchDirectLlmErrorMessage || '';
            expanded.orchNoAgentSucceededMessage = data.orchNoAgentSucceededMessage || '';
            // Default TRUE — a flow saved before this key existed keeps the behaviour it shipped with.
            expanded.allowAgentGuardrailBypass = data.allowAgentGuardrailBypass ?? true;
            expanded.enableMultiAgent = data.enableMultiAgent ?? false;
            expanded.multiAgentStrategy = data.multiAgentStrategy || 'pipeline';
            expanded.gender = data.gender || 'male';
            expanded.typingSpeed = data.typingSpeed ?? null;
            expanded.maxInputLength = data.maxInputLength ?? 200000;
            expanded.frequencyPenalty = data.frequencyPenalty ?? null;
            expanded.presencePenalty = data.presencePenalty ?? null;
            expanded.stopSequence = data.stopSequence || null;
            expanded.enableCache = data.enableCache ?? false;
            expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 1440;
            expanded.cacheScope = data.cacheScope || 'flow';
            expanded.enableSemanticCache = data.enableSemanticCache ?? false;
            expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
            expanded.showLineName = data.showLineName ?? false;
            expanded.loadingBar = data.loadingBar ?? false;
            expanded.loadingBarDuration = data.loadingBarDuration || 0;
            expanded.monitorLineUserId = data.monitorLineUserId || null;
            expanded.monitorSocialID = data.monitorSocialID || null;
            expanded.forbiddenWords = data.forbiddenWords || [''];
            expanded.enableTime = data.enableTime ?? null;
            expanded.timeZone = data.timeZone || null;
            expanded.enableTransactionId = data.enableTransactionId ?? null;
            expanded.enableInputMessageReplace = data.enableInputMessageReplace ?? false;
            expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
            expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
            break;
        case 'agent':
            expanded.agentName = data.agentName || '';
            expanded.agentDescription = data.agentDescription || '';
            expanded.personaPrompt = data.personaPrompt || '';
            expanded.agentFinalSummaryTemplate = data.agentFinalSummaryTemplate || '';
            expanded.selectedTools = data.selectedTools || [];
            expanded.enableAgentToolDecision = data.enableAgentToolDecision ?? false;
            expanded.useCustomAIModel = data.useCustomAIModel ?? false;
            expanded.agentProvider = data.agentProvider || '';
            expanded.agentModel = data.agentModel || null;
            expanded.agentTemperature = data.agentTemperature ?? 0;
            expanded.agentMaxTokens = data.agentMaxTokens ?? null;
            expanded.agentTopP = data.agentTopP ?? 1;
            expanded.agentFrequencyPenalty = data.agentFrequencyPenalty ?? null;
            expanded.agentPresencePenalty = data.agentPresencePenalty ?? null;
            expanded.agentStopSequence = data.agentStopSequence || null;
            expanded.agentStreamResponse = data.agentStreamResponse ?? false;
            expanded.agentJsonResponse = data.agentJsonResponse ?? false;
            expanded.agentGender = data.agentGender || 'male';
            expanded.agentTypingSpeed = data.agentTypingSpeed ?? null;
            expanded.agentMaxInputLength = data.agentMaxInputLength ?? 200000;
            expanded.agentMemoryTurn = data.agentMemoryTurn ?? null;
            expanded.agentMemoryDuration = data.agentMemoryDuration ?? null;
            expanded.agentShortTermMemory = data.agentShortTermMemory ?? 200;
            expanded.agentLongTermMemory = data.agentLongTermMemory ?? false;
            expanded.agentEnableTime = data.agentEnableTime ?? false;
            expanded.agentTimeZone = data.agentTimeZone || null;
            expanded.agentEnableTransactionId = data.agentEnableTransactionId ?? false;
            expanded.agentEnableLanguageReplace = data.agentEnableLanguageReplace ?? false;
            expanded.agentEnableInsurance = data.agentEnableInsurance ?? false;
            // Hosted tools — twin ของ Flow.vue expandNodeData; ลืมที่นี่ = หน้า Flow Compare แสดงไม่ครบ
            expanded.toolsConfig = data.toolsConfig || null;
            expanded.knowledgeConnections = data.knowledgeConnections || {
                documents: { nodeId: null, description: '' },
                databaseIntegration: { nodeId: null, description: '' },
                knowledgeApi: { nodeId: null, description: '' },
                products: { nodeId: null, description: '' },
                sharepoint: { nodeId: null, description: '' }
            };
            break;
        // Tax Planning Agent — deliberately only THREE keys.
        // Model / prompts / memory / quick-reply / guardrail all live in tax.TaxPlanningSettingsV2 (the central
        // settings the Tax Playground previews), so adding any of them here would let a node quietly override the
        // one place an admin is told to configure this feature.
        case 'taxPlanningAgent':
            expanded.agentName = data.agentName || '';
            expanded.agentDescription = data.agentDescription || '';
            expanded.bypassGuardrailWhenAlone = data.bypassGuardrailWhenAlone ?? true;
            expanded.taxKnowledgeEnabled = data.taxKnowledgeEnabled ?? false;
            expanded.taxKnowledgeSelections = Array.isArray(data.taxKnowledgeSelections)
                ? data.taxKnowledgeSelections : [];
            break;
        case 'metaDocuments':
            expanded.metaDocSelections = Array.isArray(data.metaDocSelections)
                ? data.metaDocSelections : [];
            expanded.topNChunks = data.topNChunks || 5;
            expanded.metaDocDescription = data.metaDocDescription || '';
            break;
        case 'tool':
            expanded.toolName = data.toolName || '';
            expanded.toolDescription = data.toolDescription || '';
            expanded.toolType = data.toolType || 'master';
            expanded.masterToolId = data.masterToolId || null;
            expanded.endpoint = data.endpoint || '';
            expanded.method = data.method || 'GET';
            expanded.headers = data.headers || [];
            expanded.body = data.body || '';
            expanded.textContent = data.textContent || '';
            break;
        case 'router':
            expanded.routerName = data.routerName || '';
            expanded.routerPrompt = data.routerPrompt || '';
            expanded.confidenceThreshold = data.confidenceThreshold ?? 0.5;
            expanded.routerOutputs = data.routerOutputs || [];
            break;
        case 'advancedRouter':
            expanded.advancedRouterName = data.advancedRouterName || '';
            expanded.advancedRouterPrompt = data.advancedRouterPrompt || '';
            expanded.advancedRouterConfidenceThreshold = data.advancedRouterConfidenceThreshold ?? 0.5;
            expanded.advancedRouterMaxDepth = data.advancedRouterMaxDepth ?? 2;
            expanded.advancedRouterOutputs = data.advancedRouterOutputs || [];
            expanded.advancedRouterCustomSchemaEnabled = data.customSchemaEnabled ?? false;
            expanded.advancedRouterSchemaTemplateText = JSON.stringify(
                data.customSchemaTemplate || getDefaultAdvancedRouterSchemaTemplate(),
                null, 2
            );
            expanded.advancedRouterIntentField = data.schemaFieldMapping?.intentField || 'capability_intent';
            expanded.advancedRouterConfidenceField = data.schemaFieldMapping?.confidenceField || 'confidence';
            expanded.advancedRouterSubIntentsField = data.schemaFieldMapping?.subIntentsField || 'intents';
            expanded.advancedRouterSchemaError = '';
            expanded.enableCache = data.enableCache ?? false;
            expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 1440;
            expanded.enableSemanticCache = data.enableSemanticCache ?? false;
            expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
            expanded.similarityMargin = data.similarityMargin ?? 0.05;
            break;
        case 'reformatTTS':
            expanded.provider = data.provider || null;
            expanded.model = data.model || null;
            expanded.temperature = data.temperature ?? null;
            expanded.maxTokens = data.maxTokens ?? null;
            expanded.systemPrompt = data.systemPrompt || '';
            expanded.streamResponse = data.streamResponse ?? false;
            expanded.jsonResponse = data.jsonResponse ?? false;
            expanded.enableCache = data.enableCache ?? false;
            expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 60;
            expanded.enableSemanticCache = data.enableSemanticCache ?? false;
            expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
            expanded.similarityMargin = data.similarityMargin ?? 0.05;
            expanded.processingMode = data.processingMode || 'LLM';
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
            break;
        case 'ssmlFormat':
            expanded.provider = data.provider || null;
            expanded.model = data.model || null;
            expanded.temperature = data.temperature ?? null;
            expanded.maxTokens = data.maxTokens ?? null;
            expanded.systemPrompt = data.systemPrompt || '';
            expanded.streamResponse = data.streamResponse ?? false;
            expanded.jsonResponse = data.jsonResponse ?? false;
            expanded.enableCache = data.enableCache ?? false;
            expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 60;
            expanded.enableSemanticCache = data.enableSemanticCache ?? false;
            expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
            expanded.similarityMargin = data.similarityMargin ?? 0.05;
            expanded.processingMode = data.processingMode || 'RuleBased';
            expanded.enableTransactionId = data.enableTransactionId ?? false;
            break;
        case 'queryRewriter':
            expanded.queryRewriterMode = data.queryRewriterMode || 'single';
            expanded.queryRewriterProvider = data.queryRewriterProvider || null;
            expanded.queryRewriterModel = data.queryRewriterModel || null;
            expanded.queryRewriterApiVersion = data.queryRewriterApiVersion || null;
            expanded.queryRewriterTemperature = data.queryRewriterTemperature ?? null;
            expanded.queryRewriterMaxTokens = data.queryRewriterMaxTokens ?? null;
            expanded.queryRewriterTopP = data.queryRewriterTopP ?? null;
            expanded.queryRewriterFrequencyPenalty = data.queryRewriterFrequencyPenalty ?? null;
            expanded.queryRewriterPresencePenalty = data.queryRewriterPresencePenalty ?? null;
            expanded.queryRewriterSystemPrompt = data.queryRewriterSystemPrompt || '';
            expanded.queryRewriterMemoryTurn = data.queryRewriterMemoryTurn ?? null;
            expanded.queryRewriterMemoryDuration = data.queryRewriterMemoryDuration ?? null;
            expanded.queryRewriterStreamResponse = data.queryRewriterStreamResponse ?? false;
            expanded.queryRewriterJsonResponse = data.queryRewriterJsonResponse ?? false;
            break;
        case 'flowTool':
            expanded.flowToolName = data.flowToolName || 'Flow Tool';
            expanded.flowToolDescription = data.flowToolDescription || '';
            expanded.internalNodes = (data.internalNodes || []).map(n => ({
                id: n.id,
                type: 'flow-node',
                position: n.position,
                data: n.data
            }));
            expanded.internalEdges = (data.internalEdges || []).map(e => ({
                id: e.id,
                source: e.source,
                target: e.target,
                sourceHandle: e.sourceHandle || 'output',
                targetHandle: e.targetHandle || 'input',
                type: 'flow-edge',
                markerEnd: 'arrowclosed'
            }));
            expanded.internalNodeCount = data.internalNodeCount || 0;
            expanded.showWarning = data.showWarning ?? false;
            break;
    }
    return expanded;
}

/**
 * The id of Contact Agent V2's SECOND input handle — the one on top, reserved for an Orchestrator's
 * `agents-bottom`. Its left-hand handle keeps the plain id 'input' that every other node uses, so ordinary
 * flow edges land on V2 exactly as they land on Contact Agent (V1).
 *
 * Only V2 ('contactAgentV2') has this handle: Contact Agent (V1, 'contactAgent') is deliberately NOT an
 * Orchestrator agent kind (rolled back 2026-09-15) and keeps just its left input.
 *
 * 🔴 Flow.vue's Rule 3 in `validateConnection` is an ALLOWLIST of target handle ids — anything not on it is
 * assumed to be an output handle and refused. This id has to stay listed there or the top handle silently
 * becomes unconnectable. FlowNode.vue renders the handle with this same literal id.
 */
export const CONTACT_AGENT_V2_TOP_HANDLE = 'input-top';

/**
 * Button Style node — the style JSON seeded into a new node, one per marker. The agent's target (what it
 * wrote inside the parentheses of `|productbutton|[label](target)`) always replaces `value`; every other
 * key is handed to the app as written. Twin of ButtonStyleRewriter.DefaultProductStyle /
 * DefaultServiceStyle in BAL — a blank field on the node falls back to those same defaults on the server.
 */
const BUTTON_STYLE_DEFAULT_OBJECTS = {
    productButtonStyle: {
        value: '', labelColor: '#FFFFFF', labelFontSize: 16, labelFontWeight: 'semibold', labelTextAlign: 'left',
        fillColor: '#0B5FFF', cornerRadius: 12, height: 48, paddingLeft: 16, paddingRight: 16,
    },
    serviceButtonStyle: {
        value: '', labelColor: '#0B5FFF', labelFontSize: 16, labelFontWeight: 'medium', labelTextAlign: 'center',
        fillColor: '#FFFFFF', borderColor: '#0B5FFF', borderWidth: 1, cornerRadius: 12, height: 48,
    },
};
export const BUTTON_STYLE_DEFAULTS = Object.freeze({
    productButtonStyle: JSON.stringify(BUTTON_STYLE_DEFAULT_OBJECTS.productButtonStyle, null, 2),
    serviceButtonStyle: JSON.stringify(BUTTON_STYLE_DEFAULT_OBJECTS.serviceButtonStyle, null, 2),
});

/**
 * Convert flat server-format flow details into VueFlow nodes + edges.
 *
 * @param {Array} flatNodes - Array of flat node objects from server (flowDetails)
 * @param {string} MarkerType_ArrowClosed - MarkerType.ArrowClosed value from VueFlow
 * @returns {{ nodes: Array, edges: Array }} VueFlow-compatible nodes and edges
 */
export function convertFromFlatStructure(flatNodes, MarkerType_ArrowClosed) {
    const flowNodes = [];
    const flowEdges = [];

    flatNodes.forEach(flatNode => {
        const position = typeof flatNode.position === 'string'
            ? JSON.parse(flatNode.position) : flatNode.position;
        const data = typeof flatNode.data === 'string'
            ? JSON.parse(flatNode.data) : flatNode.data;
        const targets = typeof flatNode.targets === 'string'
            ? JSON.parse(flatNode.targets) : (flatNode.targets || []);

        const expandedData = expandNodeData(flatNode.nodeType, data);

        const flowNode = {
            id: flatNode.id,
            type: 'flow-node',
            position: position,
            data: {
                type: flatNode.nodeType,
                label: data.label,
                ...expandedData
            },
            selected: false
        };
        flowNodes.push(flowNode);

        if (targets && Array.isArray(targets)) {
            targets.forEach((target) => {
                const edge = {
                    id: `e${flatNode.id}-${target.nodeId}-${generateGuid()}`,
                    source: flatNode.id,
                    target: target.nodeId,
                    sourceHandle: target.sourceHandle || null,
                    targetHandle: target.targetHandle || 'input',
                    type: 'flow-edge',
                    markerEnd: MarkerType_ArrowClosed,
                    animated: false,
                    showButton: false
                };
                flowEdges.push(edge);
            });
        }
    });

    return { nodes: flowNodes, edges: flowEdges };
}

/**
 * Node type display info map — provides human-readable label, icon, and color for each node type.
 */
export const NODE_TYPE_INFO = {
    messageInput:       { label: 'Message Input',       icon: 'mdi-message-text',           color: 'blue' },
    response:           { label: 'Response',             icon: 'mdi-message-reply',          color: 'green' },
    model:              { label: 'AI Model',             icon: 'mdi-robot',                  color: 'purple' },
    taxPlanning:        { label: 'Tax Planning Form',    icon: 'mdi-clipboard-text-clock',   color: 'deep-purple' },
    taxPlanningV2:      { label: 'Tax Planning Form V2', icon: 'mdi-clipboard-text-clock',   color: 'teal' },
    intentAI:           { label: 'Intent AI',            icon: 'mdi-brain',                  color: 'deep-purple' },
    aiVoice:            { label: 'AI Voice',             icon: 'mdi-microphone',             color: 'pink' },
    reformatTTS:        { label: 'Reformat TTS',         icon: 'mdi-text-to-speech',         color: 'pink' },
    ssmlFormat:         { label: 'SSML Format',          icon: 'mdi-xml',                    color: 'pink' },
    queryRewriter:      { label: 'Query Rewriter',       icon: 'mdi-pencil-outline',         color: 'deep-purple' },
    intent:             { label: 'Intent',               icon: 'mdi-target',                 color: 'orange' },
    smartIntent:        { label: 'Smart Intent',         icon: 'mdi-lightbulb',              color: 'amber' },
    router:             { label: 'Router',               icon: 'mdi-directions-fork',        color: 'teal' },
    advancedRouter:     { label: 'Advanced Router',      icon: 'mdi-source-branch',          color: 'teal' },
    condition:          { label: 'Condition',            icon: 'mdi-help-rhombus',           color: 'indigo' },
    jsonCondition:      { label: 'JSON Condition',       icon: 'mdi-code-json',              color: 'indigo' },
    switch:             { label: 'Switch',               icon: 'mdi-swap-horizontal',        color: 'cyan' },
    delay:              { label: 'Delay',                icon: 'mdi-timer-sand',             color: 'grey' },
    apiTrigger:         { label: 'API Trigger',          icon: 'mdi-api',                    color: 'red' },
    apiRequest:         { label: 'API Request',          icon: 'mdi-cloud-upload',           color: 'red' },
    contactAgent:       { label: 'Contact Agent',        icon: 'mdi-headset',                color: 'light-blue' },
    contactAgentV2:     { label: 'Contact Agent V2',     icon: 'mdi-headset',                color: 'light-blue' },
    guardrailAI:        { label: 'Guardrail AI',         icon: 'mdi-shield-check',           color: 'red' },
    documents:          { label: 'Documents',            icon: 'mdi-file-document-multiple', color: 'brown' },
    databaseIntegration:{ label: 'Database',             icon: 'mdi-database',               color: 'blue-grey' },
    apiknowledge:       { label: 'API Knowledge',        icon: 'mdi-cloud-search',           color: 'brown' },
    products:           { label: 'Products',             icon: 'mdi-shopping',               color: 'green' },
    sharepoint:         { label: 'SharePoint',           icon: 'mdi-microsoft-sharepoint',   color: 'teal' },
    metaDocuments:      { label: 'Meta Documents',       icon: 'mdi-bookshelf',              color: 'indigo' },
    mutualFund:         { label: 'Mutual Fund',          icon: 'mdi-chart-box-outline',      color: 'teal' },
    email:              { label: 'Email',                icon: 'mdi-email',                  color: 'red' },
    orchestrator:       { label: 'Orchestrator',         icon: 'mdi-sitemap',                color: 'deep-purple' },
    agent:              { label: 'Agent',                icon: 'mdi-account-cog',            color: 'blue' },
    taxPlanningAgent:   { label: 'Tax Planning Agent',   icon: 'mdi-clipboard-text-clock',   color: 'indigo' },
    tool:               { label: 'Tool',                 icon: 'mdi-wrench',                 color: 'orange' },
    flowTool:           { label: 'Flow Tool',            icon: 'mdi-chart-timeline-variant', color: 'purple' },
    interestOptimizer:  { label: 'Interest Optimizer',   icon: 'mdi-chart-line',             color: 'green' },
    interestOptimizerV2:{ label: 'Interest Optimizer V2', icon: 'mdi-calculator-variant',    color: 'amber' },
    loanInterest:       { label: 'Loan Interest',        icon: 'mdi-cash-multiple',          color: 'green' },
    loanInterestV2:     { label: 'Loan Interest V2',     icon: 'mdi-home-analytics',         color: 'green' },
    serviceFallback:    { label: 'Service Fallback',     icon: 'mdi-alert-circle',           color: 'orange' },
    buttonStyle:        { label: 'Button Style',         icon: 'mdi-gesture-tap-button',     color: 'purple' },
    flowInput:          { label: 'Flow Input',           icon: 'mdi-import',                 color: 'blue' },
    flowOutput:         { label: 'Flow Output',          icon: 'mdi-export',                 color: 'green' },
};

/**
 * Get human-readable label for a node type
 * @param {string} nodeType
 * @returns {string}
 */
export function getNodeTypeLabel(nodeType) {
    return NODE_TYPE_INFO[nodeType]?.label || nodeType;
}
